import {React, useState, useEffect, useRef, useCallback, Fragment} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import constants from '../../../constants.js';
import styles from './matchupwizard.module.css';
import api from '../../../routes/api.js';
import utils from '../../../utils/utils.js';

import { useApiProvider } from '../../../providers/ApiProvider.js';
import { useLeagueTypeProvider } from '../../../providers/LeagueTypeProvider';

import Rosters from '../Rosters/Rosters.js';
import Scorecard from '../Scorecard/Scorecard.js';
import Statistics from '../Statistics/Statistics.js';
import InputModal from '../../common/InputModal/InputModal.js';
import LineupModal from '../../common/LineupModal/LineupModal.js';

import { lineupTemplates } from './lineupTemplates.js';

const MatchupWizard = () => {
  const { setDialog, isLoading, setIsLoading, LoadingSpinner } = useApiProvider();

  const navigate = useNavigate();
  const path = useLocation();

  const { leagueType } = useLeagueTypeProvider();

  const loginDialogRef = useRef(null);
  const lineupDialogRef = useRef(null);

  const matchupObj = path.state != null ? path.state.matchup: {};

  const selectedLocation = path.state != null ? path.state.selectedLocation : {};
  const selectedDivision = path.state != null ? path.state.selectedDivision : {};

  // "" for not logged in, "home" for home team login, "away" for away team login, "both" for admin
  const [loggedInUser, setLoggedInUser] = useState("");
  const [loginCode, setLoginCode] = useState();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isSwapping, setIsSwapping] = useState(false);
  const [swappingPlayer, setSwappingPlayer] = useState({});
  const [isLineupLocked, setIsLineupLocked] = useState(true);
  // Re-render the parent after a clean swap
  const [refreshKey, setRefreshKey] = useState(0);
  const handleCleanSwap = () => setRefreshKey((k) => k + 1);

  const [players, setPlayers] = useState();
  const [genderPlayers, setGenderPlayers] = useState();

  const [localRosterPlayers, setLocalRosterPlayers] = useState({
    homeMalePlayer1 : null, homeMalePlayer2 : null, homeMalePlayer3 : null, homeMalePlayer4 : null, homeMalePlayer5 : null,
    homeFemalePlayer1 : null, homeFemalePlayer2 : null, homeFemalePlayer3 : null, homeFemalePlayer4 : null, homeFemalePlayer5 : null,
    awayMalePlayer1 : null, awayMalePlayer2 : null, awayMalePlayer3 : null, awayMalePlayer4 : null, awayMalePlayer5 : null,
    awayFemalePlayer1 : null, awayFemalePlayer2 : null, awayFemalePlayer3 : null, awayFemalePlayer4 : null, awayFemalePlayer5 : null,
  })

  // This must always default to rosters because the state that gets set there is passed to the other pages.
  // I know it's awful
  const [currentPage, setCurrentPage] = useState(constants.lineupPages[0]);
  // games stores the initially loaded games/games right after saving/submitting. It's aligned with the values currently in the back end
  // dirtyGames stores changes the user has made but hasn't saved/submitted yet.
  const [games, setGames] = useState({});
  const [dirtyGames, setDirtyGames] = useState({});
  // This ref is used to track the history of changes for the undo button
  const history = useRef([]);
  // Track history length in state so we know when to disable the undo button
  const [historyLength, setHistoryLength] = useState(0);
  const [matchupResults, setMatchupResults] = useState(null); 

  const intervalRef = useRef(null);
  const intervalStartedRef = useRef(false);

  const goBack = () => {
    navigate('/' + leagueType + '/lineups', { replace: true });
  }

  const changePage = (e) => {
    setCurrentPage(e.currentTarget.innerHTML);
  }

  const openLoginDialog = () => {
    if (loginDialogRef.current){
      loginDialogRef.current.style.display = 'flex';
      loginDialogRef.current.showModal();
    }
  }

  const closeLoginDialog = () => {
    if (loginDialogRef.current){
      loginDialogRef.current.style.display = 'none';
      loginDialogRef.current.close();
    }
  }

  const login = async (e) => {
    //This technically gets called onChange for the input text also. Skip if not submitting
    if ((e.type=='keydown' && e.keyCode==13) || e.type=="click"){
      setIsLoggingIn(true);
      try {
        const baseUrl = utils.getBaseUrl(leagueType);

        const requestBody = JSON.stringify({code: loginCode});
        const loginResponse = await api.post(
          baseUrl, 
          constants.uri.matchupCode(selectedDivision[constants.divisionId], matchupObj[constants.matchupId]),
          requestBody);
        if (typeof loginResponse != "string") throw loginResponse;

        if (loginResponse == "none"){
          setDialog({
            title: "Incorrect team code", 
            code: 403, 
            message: "Please enter a correct code", 
            isError: true});
            setIsLoggingIn(false);
            setIsLineupLocked(true);
        }
        else {
          setDialog({
            title: "Login Successful", 
            code: 200, 
            message: "", 
            isError: false});

            closeLoginDialog();
            setIsLoggingIn(false);
            if (leagueType !== constants.pages.localLeagues) setIsLineupLocked(toggleLineupLock(loginResponse));
        }


        setLoggedInUser(loginResponse == "none" ? "" : loginResponse);
        setIsLoggingIn(false);
      }
      catch (err) {
        console.error(err);
        setDialog({
          title: err.title ? err.title : constants.messages.errorMessage, 
          code: err.status ? err.status : "N/A", 
          message: err.message, 
          isError: true});
          setLoginCode(null);
        setLoggedInUser("");
        setIsLoggingIn(false);
      }
    }
  }
  const logout = () => {
    setLoggedInUser("");
    setLoginCode(null);
    setIsLineupLocked(true);
  }

  const toggleLineupLock = (user) => {
    // Locked if not logged in, admin never locks
    if (user === "") return true;
    if (user === constants.both) return false;

    if (user === constants.home && matchupResults[constants.matchup][constants.api.lineupRestrictedHome]) return true;
    if (user === constants.away && matchupResults[constants.matchup][constants.api.lineupRestrictedAway]) return true;

    return false;
  };

  const onLineupClick = () => {
    openLineupDialog(); 
  }

  const openLineupDialog = () => {
    if (lineupDialogRef.current){
      lineupDialogRef.current.style.display = 'flex';
      lineupDialogRef.current.showModal();
    }
  }
  const closeLineupDialog = () => {
    if (lineupDialogRef.current){
      lineupDialogRef.current.style.display = 'none';
      lineupDialogRef.current.close();
    }
  }

  const saveLocalLineup = async () => {
    setIsLoading(true);
    try {
      const baseUrl = utils.getBaseUrl(leagueType);
      const request = Object.fromEntries(
        Object.entries(localRosterPlayers).map(([key, value]) => [key, value === "" ? null : value])
      );
      request[constants.matchupId] = matchupObj[constants.matchupId];

      const saveResponse = await api.post(baseUrl, constants.uri.localPlayerPosition(selectedDivision[constants.divisionId], matchupObj[constants.matchupId]), JSON.stringify(request));
      
      // Strip unwanted keys before setting state
      delete saveResponse[constants.id];
      delete saveResponse[constants.matchupId];

      // The generateLineup method handles if we actually need to call the API or not
      generateLineup(saveResponse);

      setLocalRosterPlayers(saveResponse);
      setDialog({
        title: "Lineup saved", 
        code: 200, 
        message: "", 
        isError: false});
      closeLineupDialog();
      setIsLoading(false);
    }
    catch (err){
      console.error(err);
      setDialog({
        title: err.title ? err.title : constants.messages.errorMessage, 
        code: err.code ? err.code : "N/A", 
        message: err.message, 
        isError: true})
      setIsLoading(false);
    }
  }

  const generateLineup = async (players) => {
    // If the both teams have not set their lineup do nothing. For admins display a message why
    const template = utils.getLineupTemplate(players);
    if (!lineupTemplates.hasOwnProperty(template)){
      if (loggedInUser === constants.both) {
        setDialog({
          title: "Lineup not generated", 
          code: 422, 
          message: "Missing players from one/both teams", 
          isError: true}); 
      }
      return
    }
      
    const baseUrl = utils.getBaseUrl(leagueType);

    try {
      const request = {}
      const lineup = lineupTemplates[template].map((game) => {
        return {
          [constants.gameNumber]: game[constants.gameNumber],
          fields: {
            [constants.homePlayer1]: players[game[constants.homePlayer1]],
            [constants.homePlayer2]: players[game[constants.homePlayer2]],
            [constants.awayPlayer1]: players[game[constants.awayPlayer1]],
            [constants.awayPlayer2]: players[game[constants.awayPlayer2]],
          }
        }
      });
      request[constants.loginCode] = loggedInUser;
      request[constants.lineups] = lineup;

      const lineupResponse = await api.patch(baseUrl, constants.uri.matchup(matchupObj[constants.matchupId]), JSON.stringify(request));
      if (lineupResponse.errors !== null) throw lineupResponse;
      if (loggedInUser === constants.both) {
        setDialog({
          title: "Lineup automatically generated", 
          code: 200, 
          message: "", 
          isError: false}); 
      }
           
      setIsLoading(false);
    }
    catch(err) {
      console.error(err);
      setDialog({
        title: err.title ? err.title : constants.messages.errorMessage, 
        code: err.code ? err.code : "N/A", 
        message: err.message, 
        isError: true})
      setIsLoading(false);
    }
  }

  const updateDirtyGames = useCallback((newGames) => {
    history.current.push(dirtyGames);
    setHistoryLength(history.current.length);
    setDirtyGames(newGames);
  }, [dirtyGames]);

  const clearHistory = useCallback(() => {
    history.current = [];
    setHistoryLength(0);
  }, []);

  const undo = useCallback(() => {
    if (!history.current.length) return;
    setDirtyGames(history.current.pop());
    setHistoryLength(history.current.length);
  }, []);

  // Players can technically be on the roster for both teams. Once the lineup has been set, remove them 
  // from the other team so they don't show up twice on the statistics page
  const filterDuplucatePlayers = (players, lineups, homeTeamId, awayTeamId) => {
    const playerMap = {}; // playerId -> 'home' | 'away'
    
    // Make a map of what team each player played for
    lineups.forEach((game) => {
      [game[constants.homePlayer1], game[constants.homePlayer2]].forEach((id) => {
        if (id) playerMap[id] = 'home';
      });
      [game[constants.awayPlayer1], game[constants.awayPlayer2]].forEach((id) => {
        if (id) playerMap[id] = 'away';
      });
    });

    return players.filter((player) => {
      const actualSide = playerMap[player[constants.playerId]];

      // player not found in any lineup game — nothing to dedupe, keep as-is
      if (!actualSide) return true;

      const expectedTeamId = actualSide === 'home' ? homeTeamId : awayTeamId;
      return player[constants.teamId] === expectedTeamId;
    });
  }

  const addPenaltyCount = (response) => {
    response[constants.teamStatistics][constants.awayTeam][constants.penaltyGamesCount] = 0;
    response[constants.teamStatistics][constants.homeTeam][constants.penaltyGamesCount] = 0;

    response.penalties.$values.forEach((penalty) => {
      if (penalty[constants.teamId] === response[constants.matchup][constants.awayTeam][constants.teamId] && penalty[constants.api.gamePunishment] > 0){
        response[constants.teamStatistics][constants.awayTeam][constants.penaltyGamesCount] += penalty[constants.api.gamePunishment]
      } else if (penalty[constants.teamId] === response[constants.matchup][constants.homeTeam][constants.teamId] && penalty[constants.api.gamePunishment] > 0){
        response[constants.teamStatistics][constants.homeTeam][constants.penaltyGamesCount] += penalty[constants.api.gamePunishment]
      }
    });
  }

  const getMatchup = async (resetHistory) => {
    setIsLoading(true);
    try {
      const baseUrl = utils.getBaseUrl(leagueType);

      // For now we need to call this so we can get the player ranking
      const matchupResponse = await api.fetch(baseUrl, constants.uri.matchupResults(selectedDivision[constants.divisionId], matchupObj[constants.matchupId]));
      if (!matchupResponse.hasOwnProperty([constants.matchupPlayerStats])) throw matchupResponse;

      const matchupPlayers = matchupResponse[constants.matchupPlayerStats][constants.values];
      const lineups = matchupResponse[constants.lineups][constants.lineups][constants.values];

      // These two "error" paths are mutually exclusive. 
      if (lineups.hasOwnProperty("errors") && lineups.errors != null) {
        setDialog({
          title: lineups.errors.split(".")[0],
          code: "N/A",
          message: {message:lineups.errors.split(".")[1]},
          isError: true
        });
      }

      // Because players can play for both teams technically, we need to filter them out.
      const players = filterDuplucatePlayers(matchupPlayers, lineups, matchupResponse[constants.matchup][constants.homeTeamId], matchupResponse[constants.matchup][constants.awayTeamId]);

      // selected: determines if the player has been selected on the rosters page
      // disabled: determines if the checkbox in the rosters table is disabled
      players.forEach(p => {
        const isPlayerSelected = lineups.findIndex(obj => Object.values(obj).some(id => id === p[constants.playerId]));

        p.selected = isPlayerSelected > -1;

        // isEnabled determines if a sub has played their maximum games. If they have don't allow selection
        if (p.isEnabled) {
          // Enable/disable the user depending on who is logged in.
          if (loggedInUser == constants.both) {
            // Admins can select anyone
            p.disabled = false;
          } 
          else if (loggedInUser == constants.home ){
            // Allow select if home player and logged in as home
            p.disabled = !(p[constants.teamId] == matchupObj[constants.homeTeamId]);
          } 
          else if (loggedInUser == constants.away){
            // Allow select if away player and logged in as away
            p.disabled = !(p[constants.teamId] == matchupObj[constants.awayTeamId]);
          } 
          else {
            // Not logged in, can't do anything
            p.disabled = true;
          }
        }
        else {
          p.disabled = true;
        }
      });

      const homePlayers = players.filter((p) => p[constants.teamId] == matchupObj[constants.homeTeamId]);
      const awayPlayers = players.filter((p) => p[constants.teamId] == matchupObj[constants.awayTeamId]);
      
      // Sort them as a whole so they're sorted when we filter by male/female
      const sortedHomePlayers = homePlayers.sort((a, b) => {
        return a[constants.ranking] > b[constants.ranking] ? 1 : -1;
      })
      const sortedAwayPlayers = awayPlayers.sort((a, b) => {
        return a[constants.ranking] > b[constants.ranking] ? 1 : -1;
      })

      let homeMales = [];
      let homeFemales = []; 
      let awayMales = [];
      let awayFemales = [];
      sortedHomePlayers.forEach(player => {
        player.name = <div style={{display:'flex', flexDirection:'row', justifyContent:'flex-start', alignItems:'center', gap:'10px'}}>
            {player[constants.firstName] + " " + player[constants.lastName]}
            {utils.getPlayerStatusIcon(player)}
          </div>
        player[constants.gender] == "Male" ? homeMales.push(player) : homeFemales.push(player);
      });

      sortedAwayPlayers.forEach(player => {
        player.name = <div style={{display:'flex', flexDirection:'row', justifyContent:'flex-start', alignItems:'center', gap:'10px'}}>
            {player[constants.firstName] + " " + player[constants.lastName]}
            {utils.getPlayerStatusIcon(player)}
          </div>
        player[constants.gender] == "Male" ? awayMales.push(player) : awayFemales.push(player);
      });

      if (matchupResponse[constants.api.playerPositions] !== null) {
        const localPlayers = matchupResponse[constants.api.playerPositions];
        delete localPlayers[constants.id];
        delete localPlayers[constants.matchupId];
        setLocalRosterPlayers(localPlayers);
      }

      addPenaltyCount(matchupResponse);

      setGames(lineups);
      setDirtyGames(lineups);
      // if (resetHistory) {
      //   history.current = [];
      //   setHistoryLength(0);
      // }

      setMatchupResults(matchupResponse);
      leagueType === constants.pages.genderLeagues ? 
        setGenderPlayers({
          homePlayers: homePlayers,
          awayPlayers: awayPlayers,
        }) :
        setPlayers({
          homeMales: homeMales,
          homeFemales: homeFemales,
          awayMales: awayMales,
          awayFemales: awayFemales,
        });

      setIsLoading(false);
    } 
    catch (err) {
      console.error(err);
      setDialog({
        title: err.title ? err.title : constants.messages.errorMessage, 
        code: err.status ? err.status : "N/A", 
        message: err.message, 
        isError: true});
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (isLoading) return; // Wait for data from App.js
    
    // Log user out every 2 hours
    intervalRef.current = setInterval(() => {
      logout();
    }, (2 * 60 * 60 * 1000) );
    intervalStartedRef.current = true;

    closeLoginDialog();
    getMatchup(true);
  }, [loggedInUser, refreshKey]);

  return(
    matchupResults ? <div className={styles.Matchup}>
      <InputModal 
        ref={loginDialogRef} 
        dialogTitle={"Team Login"} 
        onSubmit={login} 
        closeDialog={closeLoginDialog} 
        isSubmitting={isLoggingIn}
        inputValue={loginCode}
        setInputValue={setLoginCode}/>

      {leagueType === constants.pages.localLeagues ? <LineupModal 
        ref={lineupDialogRef} 
        dialogTitle={loggedInUser === constants.home ? matchupObj.homeTeamName : matchupObj.awayTeamName}
        teamColor={loggedInUser === constants.home ? matchupObj[constants.homeClub].color : matchupObj[constants.awayClub].color}
        teamLogo={loggedInUser === constants.home ? matchupObj[constants.homeClub].logo : matchupObj[constants.awayClub].logo}
        homeMales={players.homeMales} awayMales={players.awayMales} homeFemales={players.homeFemales} awayFemales={players.awayFemales}
        onSave={saveLocalLineup}
        closeDialog={closeLineupDialog} 
        isSubmitting={isLoggingIn}
        players={localRosterPlayers}
        setPlayers={setLocalRosterPlayers}
        loggedInTeam={loggedInUser.toLocaleLowerCase()}/> : <></>}

      <div className={styles.Header}>
        <div className={styles.Title}>
          {/* TODO: eventually add these logos back and style them for mobile */}
          <img src={`/logos/${matchupObj[constants.awayClub].logo}.png`} className={styles.Logo}/>
          {`${matchupObj.awayTeamName} VS ${matchupObj.homeTeamName}`}
          <img src={`/logos/${matchupObj[constants.homeClub].logo}.png`} className={styles.Logo}/>
        </div>
        <div className={styles.SubTitle}> {`${selectedLocation[constants.locationName] ? selectedLocation[constants.locationName] : "location"} ${selectedDivision[constants.divisionName] ? selectedDivision[constants.divisionName] : "division"} DIVISION`}</div>
        <div className={styles.MatchDate}>{matchupObj[constants.matchupDate]}</div>
        <div className={styles.Address}>{matchupResults[constants.matchup][constants.api.address] ?? ""}</div>
        <div className={styles.TitleRow}>
          <div><button className={styles.BackButton} style={{float: 'left'}} onClick={goBack}><img className={styles.Arrow} src='/icons/arrow-left-white.svg'/></button></div>
          
          {/* Only show generate lineup button if it's local leagues AND we're on the rosters page*/}
          <div className={styles.LineupButtonDiv}>
            {leagueType===constants.pages.localLeagues && currentPage == constants.lineupPages[0] && loggedInUser !== ""
              ? <button className={styles.LineupButton} onClick={onLineupClick}>{loggedInUser === constants.both ? constants.labels.generateLineup : constants.labels.setLineup}</button>
              : <></>}
          </div>
          
          <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center'}}>
            {loggedInUser != "" ? <Fragment>
              <div style={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center'}}>
                <img style={{height:'50%', width:'50%'}} src="/icons/user.svg"/>
              </div>
              <div style={{fontWeight:'500'}}>
                {loggedInUser == "both" ? "Admin" : 
                loggedInUser == "home" ? matchupObj.homeTeamName : 
                loggedInUser == "away" ? matchupObj.awayTeamName : ""}
              </div>
            </Fragment> : <></>}
            <button className={styles.LoginButton} onClick={loggedInUser == "" ? openLoginDialog : logout}>{loggedInUser == "" ? "Login": "Logout"}</button>
          </div>

        </div>
      </div>
      
      <div className={styles.ButtonGroup}>
        <button
          disabled={isLoading}
          onClick={changePage}
          className={styles.NavButton}
          style={{background: currentPage == constants.lineupPages[0] ? constants.colors.cplYellow : constants.colors.cplDarkGray}}>
            {constants.lineupPages[0]}
        </button>
        
        <button
          disabled={isLoading}
          onClick={changePage}
          className={styles.NavButton} 
          style={{
            borderLeft:`1px solid ${constants.colors.cplNavy}`,
            borderRight:`1px solid ${constants.colors.cplNavy}`,
            background: currentPage == constants.lineupPages[1] ? constants.colors.cplYellow : constants.colors.cplDarkGray}}>
            {constants.lineupPages[1]}
        </button>

        <button
          disabled={isLoading}
          onClick={changePage}
          className={styles.NavButton}
          style={{background: currentPage == constants.lineupPages[2] ? constants.colors.cplYellow : constants.colors.cplDarkGray}}>
            {constants.lineupPages[2]}
        </button>
      </div>

      <div className={styles.Content}>
        {currentPage == constants.lineupPages[0] ?
          <Rosters 
            selectedDivision={selectedDivision}
            loggedInUser={loggedInUser}
            homeTeam={{id: matchupObj.homeTeamId, name: matchupObj.homeTeamName, color: matchupObj[constants.homeClub].color, logo: matchupObj[constants.homeClub].logo}} 
            awayTeam={{id: matchupObj.awayTeamId, name: matchupObj.awayTeamName, color: matchupObj[constants.awayClub].color, logo: matchupObj[constants.awayClub].logo}}
            players={players} setPlayers={setPlayers}
            genderPlayers={genderPlayers} setGenderPlayers={setGenderPlayers}
            isLineupLocked={isLineupLocked} 
            dirtyGames={dirtyGames} updateDirtyGames={updateDirtyGames}
            isSwapping={isSwapping} setIsSwapping={setIsSwapping}
            swappingPlayer={swappingPlayer} setSwappingPlayer={setSwappingPlayer}
            handleCleanSwap={handleCleanSwap} matchupTime={matchupResults[constants.matchup][constants.scheduledTimeUtc]}
            matchupEnded={matchupResults[constants.matchup][constants.endResult] != null}
          />
          : null}

        {currentPage == constants.lineupPages[1] ? 
          <Scorecard
            loggedInUser={loggedInUser}
            games={games}
            setGames={setGames}
            dirtyGames={dirtyGames}
            updateDirtyGames={updateDirtyGames}
            undo={undo}
            canUndo={historyLength > 0}
            clearHistory={clearHistory}
            matchupResults={matchupResults}
            updateMatch={getMatchup}
            homeTeam={{id: matchupObj.homeTeamId, name: matchupObj.homeTeamName, color: matchupObj[constants.homeClub].color, logo: matchupObj[constants.homeClub].logo}} 
            awayTeam={{id: matchupObj.awayTeamId, name: matchupObj.awayTeamName, color: matchupObj[constants.awayClub].color, logo: matchupObj[constants.awayClub].logo}}
            players={players} setPlayers={setPlayers}
            genderPlayers={genderPlayers} setGenderPlayers={setGenderPlayers}
            isSwapping={isSwapping} setIsSwapping={setIsSwapping}
            isLineupLocked={isLineupLocked}
          /> : null}

        {currentPage == constants.lineupPages[2] ? 
        <Statistics
          selectedDivision={selectedDivision}
          players={players} genderPlayers={genderPlayers}
          teamStatistics={matchupResults[constants.teamStatistics]}
          matchupResults={matchupResults[constants.matchupResults]}
          matchupId={matchupResults[constants.matchup][constants.matchupId]}
          homeClub={matchupResults[constants.matchup][constants.homeClub]} homeTeam={matchupResults[constants.matchup][constants.homeTeam][constants.teamName]}
          awayClub={matchupResults[constants.matchup][constants.awayClub]} awayTeam={matchupResults[constants.matchup][constants.awayTeam][constants.teamName]}
        /> : null}
      </div>
    </div> : <div style={{width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center'}}><LoadingSpinner/></div>
  )
}

export default MatchupWizard;